// Global route configuration
export const ROUTES = {
  // Public routes - accessible without authentication
  PUBLIC: [
    "/",
    "/home",
    "/login",
    "/signup",
    "/forgot-password",
    "/reset-password",
    "/admin-token",
    "/features/training",
    "/features/recruiting",
    "/features/performance",
    "/features/communication",
  ],

  // Protected routes - require authentication
  PROTECTED: [
    "/dashboard",
    "/journey",
    "/training",
    "/rep-training",
    "/programs",
    "/admin",
    "/reppi-admin",
    "/performance",
    "/account",
    "/verify-email",
    "/company",
    "/company-assignment",
    "/company-downline",
    "/company-users/:code",
    "/deals/add",
    "/clients",
    "/customer-support/:code",
    "/recruiting",
    "/recruiting/pipeline",
    "/recruiting/cold",
    "/pitch-review",
  ],

  // Default redirects
  DEFAULT_AUTHENTICATED_ROUTE: "/dashboard",
  DEFAULT_UNAUTHENTICATED_ROUTE: "/home",
};

// Helper functions to check route types
export const isPublicRoute = path => {
  return ROUTES.PUBLIC.includes(path);
};

export const isProtectedRoute = path => {
  return ROUTES.PROTECTED.includes(path);
};

// Module flags carried in the JWT `modules` claim. Access is gated per-module
// and is independent of the user's role:
//   - welcome_survey: unlocks the Welcome Survey page for any user type.
//   - general_suite:  unlocks every other authenticated page.
export const MODULES = {
  GENERAL_SUITE: "general_suite",
  WELCOME_SURVEY: "welcome_survey",
};

// The only path gated behind the welcome_survey module. Everything else
// requires the general_suite module.
export const WELCOME_SURVEY_PATH = "/welcome-survey";

// Paths that stay accessible regardless of module flags (role gating still
// applies where configured). Billing/subscription always shows for company
// admins (rendering a placeholder when the general suite is disabled), and the
// account page is available to every user type.
export const ALWAYS_ACCESSIBLE_PATHS = ["/subscription", "/account"];

// Decide whether the given modules object grants access to a target path.
// The Welcome Survey page requires the welcome_survey module; every other
// protected page requires the general_suite module.
export const canAccessPath = (modules, path) => {
  const mods = modules || {};
  if (ALWAYS_ACCESSIBLE_PATHS.includes(path)) {
    return true;
  }
  // The Welcome Survey page and its sub-pages (e.g. settings) share the module.
  if (
    path === WELCOME_SURVEY_PATH ||
    path.startsWith(`${WELCOME_SURVEY_PATH}/`)
  ) {
    return !!mods.welcome_survey;
  }
  return !!mods.general_suite;
};

// Get the appropriate redirect route based on authentication status and modules
export const getDefaultRoute = (isAuthenticated, user = null) => {
  if (!isAuthenticated) {
    return ROUTES.DEFAULT_UNAUTHENTICATED_ROUTE;
  }

  const modules = user?.modules || {};

  // Users without the general suite but with the welcome survey land on it.
  if (!modules.general_suite && modules.welcome_survey) {
    return WELCOME_SURVEY_PATH;
  }

  // Check if user has COMPANY_ADMIN role
  if (user && user.role === "COMPANY_ADMIN") {
    return "/company";
  }

  return ROUTES.DEFAULT_AUTHENTICATED_ROUTE;
};
